/* Notification Center — modal dialogs ported 1:1 from the standalone HTML
 * prototype (notification-center.html): the 5-step New Rule wizard
 * (Source · Targets · Severity · Recipients · Review) and the New/Edit modals
 * for Categories (icon picker), Recipient Groups (member rows) and Email
 * Templates (subject + body + token chips).  Client-only (demo module) — each
 * dialog builds a typed object and hands it to the store. */
import { useRef, useState, type ReactNode } from 'react';
import { StepIsland } from '../../components/StepIsland';
import { type NcCategory, type NcRule, type NcRecipientGroup, type NcTemplate, type Severity, type Purpose } from './data';

/* category → default purpose (HTML RULE_PURPOSE map) */
const CAT_PURPOSE: Record<string, Purpose> = {
  'Result Exceedance': 'violation', 'Sample Receipt': 'reminder', 'Sample Receipt & Condition': 'reminder',
  'Compliance & TAT': 'violation', 'QC & Workflow': 'reminder', 'Client & Sales': 'internal',
  'Subcontract & Logistics': 'reminder', 'System & Data': 'system', 'Capacity & Trend': 'internal',
};

/* ── shared modal shell ── */
export function Modal({ title, onClose, width = 520, children }: { title: string; onClose: () => void; width?: number; children: ReactNode }) {
  return (
    <div className="nc-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="nc-modal" style={{ width }}>
        <div className="nc-modal-hd"><h2>{title}</h2><button className="nc-modal-x" onClick={onClose} aria-label="Close"><i className="ti ti-x" /></button></div>
        {children}
      </div>
    </div>
  );
}
function Toggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return <span className={`nc-toggle${on ? ' on' : ''}`} role="button" onClick={onClick} />;
}

/* ════════════════════ New Rule wizard (5 steps) ════════════════════ */
const WATCH = [
  { icon: 'ti-droplet', label: 'Analysis Result', sub: 'Result vs limit/value' },
  { icon: 'ti-clock-hour-4', label: 'Date / Age', sub: 'TAT, calibration, contracts' },
  { icon: 'ti-flask', label: 'QC Batch', sub: 'QC pass/fail, batch age' },
  { icon: 'ti-users', label: 'Client Activity', sub: 'Cadence, contribution' },
  { icon: 'ti-server-2', label: 'System Event', sub: 'Import, integration, formula' },
  { icon: 'ti-chart-line', label: 'Aggregate Trend', sub: 'Backlog, capacity, volume' },
];
const SEV: { key: Severity; label: string; sub: string }[] = [
  { key: 'info', label: 'Info', sub: 'In-app only, batched into daily digest' },
  { key: 'warning', label: 'Warning', sub: 'In-app now + included in digest email' },
  { key: 'critical', label: 'Critical', sub: 'In-app + standalone email immediately' },
];
const ROLE_OPTS = ['Role: Lab Director', 'Role: QA Manager', 'Role: Analyst', 'Role: Project Manager', 'Dynamic: Client.AssignedSalesperson', 'Person: Bin Yu', 'External: Client contact'];
const CHAN_OPTS = ['In-app + Email', 'In-app only', 'Email only'];
const COND_OPTS = ['≥ Permit Limit × 1.0', '> 0 (presence)', 'is Positive', 'outside range'];
const SCOPE_OPTS = ['Drinking Water', 'Wastewater', 'All matrices'];
const STEP_LABELS = ['Source', 'Targets', 'Severity', 'Recipients', 'Review'];

interface Target { analyte: string; condition: string; scope: string; fixed?: boolean }
interface Recip { role: string; channel: string }

export function NewRuleWizard({ categories, defaultCategory, onClose, onCreate }: {
  categories: NcCategory[]; defaultCategory?: string;
  onClose: () => void; onCreate: (categoryName: string, rule: NcRule) => void;
}) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('Permit Limit Exceedance — TSS');
  const [watch, setWatch] = useState(0);
  const [category, setCategory] = useState(defaultCategory ?? categories[0]?.name ?? 'Result Exceedance');
  const [targets, setTargets] = useState<Target[]>([{ analyte: 'Lead (Pb)', condition: '[Result] ≥ [Permit] × 1.0', scope: 'Drinking Water', fixed: true }]);
  const [severity, setSeverity] = useState<Severity>('critical');
  const [ack, setAck] = useState(true);
  const [bypass, setBypass] = useState(true);
  const [suppress, setSuppress] = useState(true);
  const [recips, setRecips] = useState<Recip[]>([
    { role: 'Role: Lab Director', channel: 'In-app + Email' },
    { role: 'Role: QA Manager', channel: 'In-app + Email' },
    { role: 'Dynamic: Client.AssignedSalesperson', channel: 'In-app + Email' },
  ]);

  const goNext = () => (step < 5 ? setStep(step + 1) : create());
  function create() {
    const cat = categories.find((c) => c.name === category);
    const seq = (cat?.rules.length ?? 0) + 1;
    const recList = recips.map((r) => r.role.replace(/^(Role|Dynamic|Person|External):\s*/, '')).join(', ');
    onCreate(category, {
      name: name.trim() || 'New Rule',
      code: `NEW-${cat?.code ?? 0}-${String(seq).padStart(3, '0')}`,
      purpose: CAT_PURPOSE[category] ?? 'internal',
      severity,
      targets: targets.length ? `${targets.length} target${targets.length === 1 ? '' : 's'}` : undefined,
      recipients: recList || '—',
      ack,
      enabled: true,
    });
  }

  return (
    <Modal title="New Notification Rule" onClose={onClose} width={660}>
      {/* stepper — the ONE shared Dynamic Island (BO UI Standard §5) */}
      <div className="nc-wiz-steps">
        <StepIsland
          steps={STEP_LABELS.map((label, i) => ({
            id: label, label,
            state: step === i + 1 ? 'active' : step > i + 1 ? 'done' : 'todo',
          }))}
          onStepClick={(id) => { const n = STEP_LABELS.indexOf(id) + 1; if (n >= 1 && n <= step) setStep(n); }}
          isClickable={(st) => (STEP_LABELS.indexOf(st.id) + 1) <= step}
        />
      </div>
      <div className="nc-modal-bd" style={{ minHeight: 320 }}>
        {/* STEP 1 — Source */}
        {step === 1 && (
          <>
            <div className="nc-form-group"><label className="nc-flabel">Rule name</label><input className="nc-finput" value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div className="nc-form-group">
              <label className="nc-flabel">What should this rule watch?</label>
              <div className="nc-optcards">
                {WATCH.map((w, i) => (
                  <div key={w.label} className={`nc-optcard${watch === i ? ' selected' : ''}`} onClick={() => setWatch(i)}>
                    <i className={`ti ${w.icon}`} /><div className="nc-oc-label">{w.label}</div><div className="nc-oc-sub">{w.sub}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="nc-form-group">
              <label className="nc-flabel">Category</label>
              <select className="nc-fselect" value={category} onChange={(e) => setCategory(e.target.value)}>
                {categories.map((c) => <option key={c.code} value={c.name}>{c.name}</option>)}
              </select>
            </div>
          </>
        )}

        {/* STEP 2 — Targets */}
        {step === 2 && (
          <div className="nc-form-group">
            <label className="nc-flabel">Add one or more monitoring targets</label>
            <div className="nc-form-hint" style={{ margin: '0 0 10px' }}>Each target is one analyte/condition. The rule fires when any target matches. You can add more later from the rule detail page.</div>
            <table className="nc-tgt-table">
              <thead><tr><th style={{ width: '30%' }}>Target</th><th style={{ width: '42%' }}>Condition</th><th>Scope</th></tr></thead>
              <tbody>
                {targets.map((t, i) => (
                  <tr key={i}>
                    {t.fixed
                      ? <><td><div className="nc-tgt-name">{t.analyte}</div></td><td><span className="nc-tgt-cond">{t.condition}</span></td><td><span className="nc-tgt-scope">{t.scope}</span></td></>
                      : <>
                        <td><input className="nc-finput nc-sm" placeholder="Analyte…" value={t.analyte} onChange={(e) => setTargets((cur) => cur.map((x, j) => j === i ? { ...x, analyte: e.target.value } : x))} /></td>
                        <td><select className="nc-fselect nc-sm" value={t.condition} onChange={(e) => setTargets((cur) => cur.map((x, j) => j === i ? { ...x, condition: e.target.value } : x))}>{COND_OPTS.map((o) => <option key={o}>{o}</option>)}</select></td>
                        <td style={{ display: 'flex', gap: 6, alignItems: 'center' }}><select className="nc-fselect nc-sm" value={t.scope} onChange={(e) => setTargets((cur) => cur.map((x, j) => j === i ? { ...x, scope: e.target.value } : x))}>{SCOPE_OPTS.map((o) => <option key={o}>{o}</option>)}</select><button className="nc-iconbtn del" title="Remove" onClick={() => setTargets((cur) => cur.filter((_, j) => j !== i))}><i className="ti ti-x" /></button></td>
                      </>}
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ padding: '10px 0' }}><span className="nc-addbtn" onClick={() => setTargets((cur) => [...cur, { analyte: '', condition: COND_OPTS[0]!, scope: SCOPE_OPTS[0]! }])}><i className="ti ti-plus" /> Add monitoring target</span></div>
          </div>
        )}

        {/* STEP 3 — Severity */}
        {step === 3 && (
          <>
            <div className="nc-form-group">
              <label className="nc-flabel">How urgent is this?</label>
              <div className="nc-sevopts">
                {SEV.map((s) => (
                  <div key={s.key} className={`nc-sevopt ${s.key}${severity === s.key ? ' selected' : ''}`} onClick={() => setSeverity(s.key)}>
                    <span className={`dot ${s.key}`} /><div className="nc-sevopt-lbl">{s.label}</div><div className="nc-sevopt-sub">{s.sub}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="nc-form-group">
              <div className="nc-togrow"><div><div className="nc-togrow-text">Requires acknowledgment</div><div className="nc-togrow-sub">A person must explicitly acknowledge before this clears.</div></div><Toggle on={ack} onClick={() => setAck(!ack)} /></div>
              <div className="nc-togrow"><div><div className="nc-togrow-text">Bypass digest batching</div><div className="nc-togrow-sub">Send immediately even if severity would normally allow batching.</div></div><Toggle on={bypass} onClick={() => setBypass(!bypass)} /></div>
              <div className="nc-togrow"><div><div className="nc-togrow-text">Suppress duplicate firing</div><div className="nc-togrow-sub">Re-occurrence increments a repeat counter instead of a new alert.</div></div><Toggle on={suppress} onClick={() => setSuppress(!suppress)} /></div>
            </div>
          </>
        )}

        {/* STEP 4 — Recipients */}
        {step === 4 && (
          <div className="nc-form-group">
            <label className="nc-flabel">Who should be notified?</label>
            {recips.map((r, i) => (
              <div key={i} className="nc-recprow">
                <select value={r.role} onChange={(e) => setRecips((cur) => cur.map((x, j) => j === i ? { ...x, role: e.target.value } : x))}>{ROLE_OPTS.map((o) => <option key={o}>{o}</option>)}</select>
                <select style={{ flex: '0 0 120px' }} value={r.channel} onChange={(e) => setRecips((cur) => cur.map((x, j) => j === i ? { ...x, channel: e.target.value } : x))}>{CHAN_OPTS.map((o) => <option key={o}>{o}</option>)}</select>
                <button className="nc-iconbtn del" title="Remove" onClick={() => setRecips((cur) => cur.filter((_, j) => j !== i))}><i className="ti ti-trash" /></button>
              </div>
            ))}
            <div className="nc-addbtn" onClick={() => setRecips((cur) => [...cur, { role: ROLE_OPTS[0]!, channel: CHAN_OPTS[0]! }])}><i className="ti ti-plus" /> Add recipient</div>
          </div>
        )}

        {/* STEP 5 — Review */}
        {step === 5 && (
          <>
            <div className="nc-review">
              <div className="nc-review-row"><span className="nc-review-k">Rule name</span><span className="nc-review-v">{name.trim() || 'New Rule'}</span></div>
              <div className="nc-review-row"><span className="nc-review-k">Category</span><span className="nc-review-v">{category}</span></div>
              <div className="nc-review-row"><span className="nc-review-k">Severity</span><span className="nc-review-v"><span className={`nc-sev ${severity}`}><span className="dot" />{severity.charAt(0).toUpperCase() + severity.slice(1)}</span></span></div>
              <div className="nc-review-row"><span className="nc-review-k">Acknowledgment</span><span className="nc-review-v">{ack ? 'Required' : 'Not required'}</span></div>
              <div className="nc-review-row"><span className="nc-review-k">Targets</span><span className="nc-review-v">{targets.length} configured</span></div>
              <div className="nc-review-row"><span className="nc-review-k">Recipients</span><span className="nc-review-v">{recips.length} configured</span></div>
            </div>
            <div className="nc-form-hint">Once saved, this rule activates immediately and begins evaluating against live result data. You can disable it anytime from the Rules list without deleting it.</div>
          </>
        )}
      </div>
      <div className="nc-modal-ft">
        <button className="ca-btn" disabled={step === 1} onClick={() => setStep(step - 1)}>Back</button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="ca-btn" onClick={onClose}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
          <button className="ca-btn ca-btn-solid" onClick={goNext}>{step === 5 ? <><i className="ti ti-check" /> Create Rule</> : 'Next'}</button>
        </div>
      </div>
    </Modal>
  );
}

/* ════════════════════ Category New/Edit (icon picker) ════════════════════ */
const CAT_ICONS = ['ti-droplet-exclamation', 'ti-package-import', 'ti-clipboard-check', 'ti-flask', 'ti-users', 'ti-truck-delivery', 'ti-server-2', 'ti-trending-up', 'ti-alert-triangle', 'ti-bell', 'ti-mail', 'ti-shield-check', 'ti-clock-hour-4', 'ti-chart-line', 'ti-microscope', 'ti-droplet'];

export function CategoryForm({ initial, nextCode, nextOrder, onClose, onSave }: {
  initial?: NcCategory; nextCode: number; nextOrder: number;
  onClose: () => void; onSave: (cat: NcCategory) => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [order, setOrder] = useState(initial?.order ?? nextOrder);
  const [icon, setIcon] = useState(initial?.icon ?? 'ti-bell');
  const [active, setActive] = useState(initial?.active ?? true);
  return (
    <Modal title={initial ? 'Edit Rule Category' : 'New Rule Category'} onClose={onClose} width={560}>
      <div className="nc-modal-bd">
        <div className="nc-form-row">
          <div className="nc-form-group"><label className="nc-flabel">Name</label><input className="nc-finput" value={name} autoFocus onChange={(e) => setName(e.target.value)} placeholder="e.g. Result Exceedance" /><div className="nc-form-hint">What people see in the sidebar &amp; dropdowns. Rename anytime — it won't break rules.</div></div>
          <div className="nc-form-group"><label className="nc-flabel">Sort order</label><input className="nc-finput" type="number" value={order} onChange={(e) => setOrder(Number(e.target.value))} /><div className="nc-form-hint">Lower = higher up. Gaps of 10 let you slot a new one between two existing.</div></div>
        </div>
        <div className="nc-form-hint" style={{ margin: '-6px 0 14px' }}>The category's <strong>code is a short number, assigned automatically</strong> — you don't type it. Rules link to that number, not the name, so renaming is safe.</div>
        <div className="nc-form-group">
          <label className="nc-flabel">Icon</label>
          <div className="nc-iconpicker">
            {CAT_ICONS.map((ic) => <div key={ic} className={`nc-iconpick${icon === ic ? ' selected' : ''}`} onClick={() => setIcon(ic)}><i className={`ti ${ic}`} /></div>)}
          </div>
        </div>
        <div className="nc-togrow"><div><div className="nc-togrow-text">Active</div><div className="nc-togrow-sub">Inactive categories are hidden from the rule-configuration Category dropdown.</div></div><Toggle on={active} onClick={() => setActive(!active)} /></div>
      </div>
      <div className="nc-modal-ft"><div /><div style={{ display: 'flex', gap: 8 }}>
        <button className="ca-btn" onClick={onClose}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
        <button className="ca-btn ca-btn-solid" disabled={!name.trim()} onClick={() => onSave({ code: initial?.code ?? nextCode, name: name.trim(), icon, order, active, rules: initial?.rules ?? [] })} title="Save"><span className="tb-i">💾</span> Save</button>
      </div></div>
    </Modal>
  );
}

/* ════════════════════ Recipient Group New/Edit (member rows) ════════════════════ */
export function RecipientForm({ initial, onClose, onSave }: {
  initial?: NcRecipientGroup; onClose: () => void; onSave: (group: NcRecipientGroup) => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [members, setMembers] = useState<string[]>(initial ? initial.members.split(',').map((m) => m.trim()).filter(Boolean) : ['']);
  const update = (i: number, v: string) => setMembers((cur) => cur.map((m, j) => j === i ? v : m));
  return (
    <Modal title={initial ? 'Edit Recipient Group' : 'New Recipient Group'} onClose={onClose} width={580}>
      <div className="nc-modal-bd">
        <div className="nc-form-group"><label className="nc-flabel">Group name</label><input className="nc-finput" value={name} autoFocus onChange={(e) => setName(e.target.value)} placeholder="e.g. DW Compliance Team" /></div>
        <div className="nc-form-group">
          <label className="nc-flabel">Members</label>
          {members.map((m, i) => (
            <div key={i} className="nc-recprow">
              <input className="nc-finput" style={{ flex: 1 }} value={m} onChange={(e) => update(i, e.target.value)} placeholder="Role, dynamic field, person, or external contact" />
              <button className="nc-iconbtn del" title="Remove" onClick={() => setMembers((cur) => cur.filter((_, j) => j !== i))}><i className="ti ti-trash" /></button>
            </div>
          ))}
          <div className="nc-addbtn" onClick={() => setMembers((cur) => [...cur, ''])}><i className="ti ti-plus" /> Add member</div>
          <div className="nc-form-hint">A member can be a role, a dynamic field on the source record, a named person, or an external contact.</div>
        </div>
      </div>
      <div className="nc-modal-ft"><div /><div style={{ display: 'flex', gap: 8 }}>
        <button className="ca-btn" onClick={onClose}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
        <button className="ca-btn ca-btn-solid" disabled={!name.trim() || !members.some((m) => m.trim())} onClick={() => onSave({ name: name.trim(), members: members.map((m) => m.trim()).filter(Boolean).join(', ') || '—', channels: initial?.channels ?? ['IN-APP', 'EMAIL'], usedBy: initial?.usedBy ?? 0 })} title="Save"><span className="tb-i">💾</span> Save</button>
      </div></div>
    </Modal>
  );
}

/* ════════════════════ Email Template New/Edit (subject + body + tokens) ════════════════════ */
const TOKENS = ['{{JobId}}', '{{SampleId}}', '{{Client}}', '{{TestCode}}', '{{Parameter}}', '{{Result}}', '{{Limit}}', '{{DueDate}}'];

export function TemplateForm({ initial, categoryNames, onClose, onSave }: {
  initial?: NcTemplate; categoryNames: string[]; onClose: () => void; onSave: (tpl: NcTemplate) => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [category, setCategory] = useState(initial?.category ?? categoryNames[0] ?? '');
  const [subject, setSubject] = useState(initial?.subject ?? '');
  const [body, setBody] = useState(initial?.body ?? '');
  const [active, setActive] = useState(initial?.active ?? true);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  function insertToken(tok: string) {
    const el = bodyRef.current;
    if (!el) { setBody((b) => b + tok); return; }
    const start = el.selectionStart, end = el.selectionEnd;
    setBody((b) => b.slice(0, start) + tok + b.slice(end));
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = start + tok.length; });
  }

  return (
    <Modal title={initial ? 'Edit Email Template' : 'New Email Template'} onClose={onClose} width={620}>
      <div className="nc-modal-bd">
        <div className="nc-form-row">
          <div className="nc-form-group"><label className="nc-flabel">Template name</label><input className="nc-finput" value={name} autoFocus onChange={(e) => setName(e.target.value)} placeholder="e.g. Critical Exceedance" /></div>
          <div className="nc-form-group"><label className="nc-flabel">Category</label><select className="nc-fselect" value={category} onChange={(e) => setCategory(e.target.value)}>{categoryNames.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
        </div>
        <div className="nc-form-group"><label className="nc-flabel">Subject</label><input className="nc-finput" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="[ALPACA] {{RuleName}} — {{SampleId}}" /></div>
        <div className="nc-form-group">
          <label className="nc-flabel">Body</label>
          <textarea ref={bodyRef} className="nc-textarea" rows={6} value={body} onChange={(e) => setBody(e.target.value)} />
          <div className="nc-form-hint">Click a token to insert into the body: {TOKENS.map((t) => <span key={t} className="nc-tok" onClick={() => insertToken(t)} style={{ cursor: 'pointer' }}>{t}</span>)}</div>
        </div>
        <div className="nc-togrow"><div><div className="nc-togrow-text">Active</div><div className="nc-togrow-sub">Inactive templates fall back to the category default.</div></div><Toggle on={active} onClick={() => setActive(!active)} /></div>
      </div>
      <div className="nc-modal-ft"><div /><div style={{ display: 'flex', gap: 8 }}>
        <button className="ca-btn" onClick={onClose}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
        <button className="ca-btn ca-btn-solid" disabled={!name.trim() || !subject.trim()} onClick={() => onSave({ name: name.trim(), category, subject: subject.trim(), body: body.trim() || undefined, active })} title="Save"><span className="tb-i">💾</span> Save</button>
      </div></div>
    </Modal>
  );
}
